import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CheckIcon, ChevronLeftIcon, LeafIcon, MinusIcon, PlusIcon, TruckIcon } from 'lucide-react';
import { getProduct, products } from '../data/products';
import { useCart } from '../contexts/CartContext';
import { formatPrice } from '../utils/format';
import { ProductCard } from '../components/ProductCard';
export function ProductDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const product = slug ? getProduct(slug) : undefined;
  const [optionId, setOptionId] = useState<string | undefined>(product?.options?.[0].id);
  const [colorIndex, setColorIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const {
    addLine
  } = useCart();
  const related = useMemo(() => products.filter(p => p.category === product?.category && p.slug !== product?.slug).slice(0, 4), [product]);
  useEffect(() => {
    setOptionId(product?.options?.[0].id);
    setColorIndex(0);
    setQuantity(1);
    setAdded(false);
    window.scrollTo({
      top: 0
    });
  }, [slug, product]);
  if (!product) {
    return <div className="mx-auto max-w-2xl px-4 py-24 text-center" data-id="element-271" data-mp="3opdb98ifvhyu">
        <h1 className="font-display text-4xl font-extrabold text-encre" data-id="element-272" data-mp="3ax5idrua0b5f">Produit introuvable</h1>
        <p className="mt-3 text-encre-soft" data-id="element-273" data-mp="3fvqlq04tray3">Ce produit n'existe plus ou a changé d'adresse.</p>
        <Link to="/boutique" className="mt-6 inline-block rounded-full bg-framboise px-6 py-3 font-display font-extrabold text-white hover:bg-framboise-dark" data-id="element-274" data-mp="7g6u15jsy0l5">
          Retour à la boutique
        </Link>
      </div>;
  }
  const option = product.options?.find(o => o.id === optionId) ?? product.options?.[0];
  const unitPrice = option ? option.price : product.price;
  const colorway = product.colorways?.[colorIndex];
  const handleAdd = () => {
    addLine({
      slug: product.slug,
      name: product.name,
      optionLabel: option ? option.label : product.unit,
      colorwayName: colorway?.name,
      colorwayHex: colorway?.hex,
      unitPrice,
      quantity,
      image: product.image
    });
    setAdded(true);
    window.setTimeout(() => setAdded(false), 1800);
  };
  return <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6" data-id="element-275" data-mp="1ua0m0j4b0zsg">
      <Link to={`/boutique?cat=${product.category}`} className="inline-flex items-center gap-1.5 text-sm font-bold text-encre-soft hover:text-framboise-dark" data-id="element-276" data-mp="1pdjy9q10onfw">
        <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-277" data-mp="1fe2ytqccz5sd" /> Retour à la boutique
      </Link>

      <div className="mt-6 grid gap-10 lg:grid-cols-[1fr_0.9fr] lg:gap-14" data-id="element-278" data-mp="1bchejjxhvp85">
        <div className="overflow-hidden rounded-4xl bg-white ring-1 ring-encre/10" data-id="element-279" data-mp="16dwcdeyomvh2">
          <img src={product.image} alt={product.name} className="w-full object-cover" data-id="element-280" data-mp="16fz8mgqfhku8" />
        </div>

        <div data-id="element-281" data-mp="35oo11evu9sfr">
          <h1 className="font-display text-5xl font-extrabold leading-tight tracking-tight text-encre" data-id="element-282" data-mp="30rxjso8asqsr">
            {product.name}
          </h1>
          <p className="mt-2 text-lg text-encre-soft" data-id="element-283" data-mp="38andz8ijgf7h">{product.tagline}</p>

          <p className="mt-6 flex items-baseline gap-2" data-id="element-284" data-mp="3d9rktshzq0un">
            <span className="font-display text-4xl font-extrabold text-framboise" data-id="element-285" data-mp="3d86pi0nuqdvq">
              {formatPrice(unitPrice)}
            </span>
            <span className="text-sm font-semibold text-encre-light" data-id="element-286" data-mp="3n0m6ovrbqyfk">
              {option ? option.label : product.unit}
            </span>
          </p>

          <p className="mt-5 max-w-xl leading-relaxed text-encre-soft" data-id="element-287" data-mp="2peoshajz9f9f">{product.description}</p>

          {product.options && <fieldset className="mt-8" data-id="element-288" data-mp="2za8pk0aa2jzr">
              <legend className="text-sm font-bold text-encre" data-id="element-289" data-mp="2z9720wc2s515">Conditionnement</legend>
              <div className="mt-2.5 flex flex-wrap gap-2" data-id="element-290" data-mp="33d51571ehfu7">
                {product.options.map(opt => {
              const isActive = opt.id === option?.id;
              return <button key={opt.id} type="button" onClick={() => setOptionId(opt.id)} aria-pressed={isActive} className={['rounded-2xl px-4 py-2.5 text-sm font-bold transition-colors duration-150 ease-out', isActive ? 'bg-encre text-creme' : 'bg-white text-encre ring-1 ring-encre/15 hover:bg-creme-deep'].join(' ')} data-id="element-291" data-mp="il34cguxvjog">
                      {opt.label} · {formatPrice(opt.price)}
                    </button>;
            })}
              </div>
            </fieldset>}

          {product.colorways && <fieldset className="mt-8" data-id="element-292" data-mp="14vl3qkb9f5r6">
              <legend className="text-sm font-bold text-encre" data-id="element-293" data-mp="14wmo5fe614lk">
                Coloris —{' '}
                <span className="font-semibold text-encre-soft" data-id="element-294" data-mp="1is8bzj8nxice">
                  {colorway?.name}
                  {colorway?.code ? ` (n° ${colorway.code})` : ''}
                </span>
              </legend>
              <div className="mt-3 flex flex-wrap gap-2" data-id="element-295" data-mp="1xf731d3lyaql">
                {product.colorways.map((c, i) => {
              const isActive = i === colorIndex;
              return <button key={c.name + (c.code ?? '')} type="button" onClick={() => setColorIndex(i)} title={c.name} aria-label={`Coloris ${c.name}`} aria-pressed={isActive} className={['h-9 w-9 rounded-full ring-1 ring-inset ring-black/10 transition-transform duration-150 ease-out hover:scale-105', isActive ? 'outline outline-2 outline-offset-2 outline-encre' : ''].join(' ')} style={{
                backgroundColor: c.hex
              }} data-id="element-296" data-mp="1oio9pzrgskth" data-mpi={i} />;
            })}
              </div>
            </fieldset>}

          <div className="mt-8 flex flex-wrap items-center gap-3" data-id="element-297" data-mp="32hhr4yrysvv8">
            <div className="flex items-center rounded-full border-2 border-encre/15 bg-white" data-id="element-298" data-mp="2xoyec413q4rh">
              <button type="button" onClick={() => setQuantity(q => Math.max(1, q - 1))} className="grid h-11 w-11 place-items-center rounded-full text-encre hover:bg-creme-deep" aria-label="Diminuer la quantité" data-id="element-299" data-mp="2tmtlv8me3e6f">
                <MinusIcon className="h-4 w-4" data-id="element-300" data-mp="3hcxmd74a6xt7" />
              </button>
              <span className="w-10 text-center font-display text-lg font-extrabold" data-id="element-301" data-mp="3ce46j81iuupv">{quantity}</span>
              <button type="button" onClick={() => setQuantity(q => q + 1)} className="grid h-11 w-11 place-items-center rounded-full text-encre hover:bg-creme-deep" aria-label="Augmenter la quantité" data-id="element-302" data-mp="37ezzoo22l92p">
                <PlusIcon className="h-4 w-4" data-id="element-303" data-mp="1w27btoceh4rx" />
              </button>
            </div>

            <button type="button" onClick={handleAdd} className="inline-flex items-center gap-2 rounded-full bg-framboise px-7 py-3.5 font-display text-base font-extrabold text-white transition-colors duration-150 ease-out hover:bg-framboise-dark" data-id="element-304" data-mp="1ltta41kyki0j">
              {added ? <>
                  <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-305" data-mp="24n6w3h6p1e40" /> Ajouté au panier
                </> : <>Ajouter au panier · {formatPrice(unitPrice * quantity)}</>}
            </button>
          </div>

          <ul className="mt-6 space-y-2 text-sm text-encre-soft" data-id="element-306" data-mp="2eiihujgirll9">
            <li className="flex items-center gap-2" data-id="element-307" data-mp="2jid7yksh0759">
              <TruckIcon className="h-4 w-4 text-framboise" aria-hidden="true" data-id="element-308" data-mp="k6tv3v41qeqb" />
              Retrait gratuit à Vouziers · livraison offerte dès 49 €
            </li>
            {product.oekotex && <li className="flex items-center gap-2" data-id="element-309" data-mp="2r2xj68av33is">
                <LeafIcon className="h-4 w-4 text-framboise" aria-hidden="true" data-id="element-310" data-mp="2r1comiojrkkx" />
                Certifié Oeko-Tex® Standard 100
              </li>}
          </ul>

          {product.specs && <dl className="mt-8 grid gap-x-8 gap-y-4 border-t border-encre/10 pt-6 sm:grid-cols-2" data-id="element-311" data-mp="30v38195th4tr">
              {product.specs.map(spec => <div key={spec.label} data-id="element-312" data-mp="283jk5by9z2lf">
                  <dt className="text-xs font-bold uppercase tracking-wide text-encre-light" data-id="element-313" data-mp="284l8uj87rnlm">
                    {spec.label}
                  </dt>
                  <dd className="mt-0.5 font-semibold text-encre" data-id="element-314" data-mp="2h5j2c7l10cpy">{spec.value}</dd>
                </div>)}
            </dl>}
        </div>
      </div>

      {related.length > 0 && <section className="mt-20" data-id="element-315" data-mp="3sjn3bxq1714i">
          <h2 className="font-display text-3xl font-extrabold tracking-tight text-encre" data-id="element-316" data-mp="3silix2n4l2a4">
            Vous aimerez aussi
          </h2>
          <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-317" data-mp="3w23vobjfbqmq">
            {related.map(p => <ProductCard key={p.slug} product={p} data-id="element-318" data-mp="4vjah8g1vcxm" />)}
          </div>
        </section>}
    </div>;
}